import type { VaultItem } from '../library/vault/vaultData'
import type { Post, PostLabel } from './posts'

/** What an edit form can change on a published or queued post. */
export type EditPostPatch = {
  textHtml?: string
  labels?: PostLabel[]
  price?: number
  /** Days until expiry from publish (or now). `null` clears expiry. Omit to leave unchanged. */
  expireDays?: number | null
  media?: VaultItem[]
  previewIds?: number[]
}

/** Whole days between two ISO timestamps (queue/profile expiry chips). */
export function expireDaysBetween(fromIso: string | null | undefined, toIso: string | null | undefined): number | null {
  if (!toIso) return null
  const from = fromIso ? Date.parse(fromIso) : Date.now()
  const to = Date.parse(toIso)
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null
  const days = Math.round((to - from) / 86_400_000)
  return days > 0 ? days : null
}

export function previewIdsForPaid(media: VaultItem[], price: number, known: number[] = []): number[] {
  if (price <= 0 || media.length === 0) return []
  const kept = known.filter((id) => media.some((m) => m.id === id))
  return kept.length ? kept : [media[0]!.id]
}

/**
 * `PUT posts/<id>` body shared by feed edit and queue edit.
 * Schedule keys only when `scheduled` is set (queue / saved-for-later).
 */
export function buildPutPostBody(
  post: Pick<Post, 'text' | 'labels' | 'media' | 'price' | 'previewIds'>,
  patch: EditPostPatch,
  scheduled?: { at: string } | { later: true },
): Record<string, unknown> {
  const text = patch.textHtml ?? post.text
  const labels = patch.labels ?? post.labels
  const media = patch.media ?? post.media
  const price = patch.price ?? post.price
  const previewIds = patch.previewIds ?? previewIdsForPaid(media, price, post.previewIds)
  const body: Record<string, unknown> = {
    preview: previewIds,
    rfTag: [],
    rfGuest: [],
    rfPartner: [],
    text,
    labelIds: labels.map((l) => Number(l.id)).filter(Number.isFinite),
  }
  if (media.length || price > 0) {
    body.mediaFiles = media.map((m) => m.id)
    body.price = price
  }
  if (patch.expireDays !== undefined) {
    // OnlyFans: "Keep empty for no expiration."
    body.expireDays = patch.expireDays === null ? '' : patch.expireDays
  }
  if (scheduled) {
    body.isScheduled = 1
    if ('later' in scheduled) body.isPublishedWithPeriod = 1
    else body.scheduledDate = new Date(scheduled.at).toISOString()
  }
  return body
}

export function applyEditPatch(post: Post, patch: EditPostPatch): Post {
  const media = patch.media ?? post.media
  const price = patch.price ?? post.price
  let expiredAt = post.expiredAt
  if (patch.expireDays === null) expiredAt = null
  else if (typeof patch.expireDays === 'number' && patch.expireDays > 0) {
    const from = Date.parse(post.postedAt) || Date.now()
    expiredAt = new Date(from + patch.expireDays * 86_400_000).toISOString()
  }
  return {
    ...post,
    text: patch.textHtml ?? post.text,
    labels: patch.labels ?? post.labels,
    price,
    media,
    mediaCount: media.length,
    previewIds: patch.previewIds ?? previewIdsForPaid(media, price, post.previewIds),
    expiredAt,
  }
}
